// frontend/src/config/index.js

/**
 * Centralized configuration for the application
 * This ensures consistent usage of environment variables across the app
 */ 

// API Configuration
const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api",
  TIMEOUT: 30000, // 30 seconds
  RETRY_ATTEMPTS: 3,
};

// Authentication Configuration
const AUTH_CONFIG = {
  TOKEN_KEY: "jwtToken",
  REFRESH_TOKEN_KEY: "refreshToken",
  TOKEN_EXPIRY_KEY: "tokenExpiry",
};

// Feature Flags
const FEATURES = {
  ENABLE_ANALYTICS: process.env.REACT_APP_ENABLE_ANALYTICS === "true",
  ENABLE_NOTIFICATIONS: process.env.REACT_APP_ENABLE_NOTIFICATIONS === "true",
  MAINTENANCE_MODE: process.env.REACT_APP_MAINTENANCE_MODE === "true",
  ENABLE_UNIFIED_STOREFRONT:
    process.env.REACT_APP_ENABLE_UNIFIED_STOREFRONT === "true",
};

// External Services
const SERVICES = {
  STRIPE: {
    PUBLIC_KEY: process.env.REACT_APP_STRIPE_PUBLIC_KEY,
  },
  RECAPTCHA: {
    SITE_KEY: process.env.REACT_APP_RECAPTCHA_SITE_KEY,
  },
};

// API Endpoints
const ENDPOINTS = {
  // Auth
  AUTH: {
    LOGIN: "/auth/login",
    REFRESH: "/auth/refresh",
    LOGOUT: "/auth/logout",
    REGISTER: "/auth/register",
    VERIFY_EMAIL: "/auth/verify-email",
    FORGOT_PASSWORD: "/auth/forgot-password",
    RESET_PASSWORD: "/auth/reset-password",
  },

  // User Profile
  PROFILE: {
    GET: "/profile",
    UPDATE: "/profile",
    UPLOAD_IMAGE: "/upload/profile-image",
  },

  // Bookings
  SESSIONS: {
    AVAILABLE: "/sessions/availability",
    MY_BOOKINGS: "/sessions/myBookings",
    CREATE: "/sessions/book",
    UPDATE: "/sessions",
    CANCEL: "/sessions",
  },

  // Products
  PRODUCTS: {
    MEMBERSHIPS: "/products/memberships",
    DROP_INS: "/products/drop-ins",
    PACKAGES: "/products/packages",
  },

  // Payments
  PAYMENTS: {
    SETUP_INTENT: "/payments/create-setup-intent",
    SAVE_METHOD: "/payments/save-payment-method",
    GET_METHODS: "/payments/methods",
    DELETE_METHOD: "/payments/delete-method",
  },

  // Stripe
  STRIPE: {
    CHECKOUT: "/stripe/create-checkout-session",
    PORTAL: "/stripe/create-portal-session",
  },

  // Staff
  STAFF: {
    CUSTOMERS: "/staff/customers",
    BOOKINGS: "/staff/bookings",
    MEMBERS: "/staff/members",
  },

  THERAPIST: {
    DASHBOARD: "/therapist/dashboard",
    AVAILABILITY: "/therapist/availability",
    AVAILABILITY_OVERRIDES: "/therapist/availability-overrides",
    PREVIEW_AVAILABILITY: "/therapist/massage-availability",
  },

  // Analytics
  ANALYTICS: {
    MEMBERS: "/analytics/members",
    BOOKINGS: "/analytics/bookings",
    ACCOUNTS: "/analytics/accounts",
    MASSAGE_PAYOUTS: "/analytics/massage-payouts",
    COMMUNICATIONS: "/analytics/communications",
    INTERACTIONS: "/analytics/interactions",
    EXPORT: "/analytics/export",
  },

  // CRM
  CRM: {
    INTERACTIONS: "/crm/interactions",
    TEMPLATES: "/crm/templates",
    SEGMENTS: "/crm/segments",
    JOURNEYS: "/crm/journeys",
  },

  // Feedback
  FEEDBACK: {
    SUBMIT: "/feedback",
    GET: "/feedback",
  },

  // Suites
  SUITES: {
    GET_ALL: "/suites",
    GET_ONE: "/suites",
    AVAILABILITY: "/suites",
  },

  // Contact
  CONTACT: {
    SUBMIT: "/contact",
  },

  // INTRO-OFFER-001 — customer claim endpoint (Story B).
  // Profile block is surfaced additively on /api/profile (Story A).
  INTRO_OFFER: {
    CLAIM: "/intro-offer/claim",
  },

  // MAILCHIMP-COMMS-001 — Operational Broadcast (Slice 1A).
  // BE landed on PR #156. Family lives under /api/comms/broadcast:
  //   POST   /broadcast      — send (template OR freeform), returns 202
  //   GET    /broadcast      — paginated history (staff own / admin all)
  //   GET    /broadcast/:id  — read one (staff own / admin any)
  // RBAC: admin + staff via authorizeBroadcastSender middleware.
  // Recipient cap (BE-locked, env-overridable): staff 50, admin 500.
  // Validation errors of note: RECIPIENT_CAP_EXCEEDED { cap, role, attempted }
  // and BODY_SANITIZED_TO_EMPTY (DOMPurify stripped all content).
  COMMS: {
    BROADCAST: "/comms/broadcast",
  },

  // PROMO-RF-B / ADR-029 D2+D5 — Stripe staff-console promo-code surface.
  // Distinct from the LEGACY in-house /api/promo-codes engine (left intact
  // for PROMO-RF-H to delete LAST per ADR-029 D4 — do NOT consume that here).
  // BE landed on PR #181. Family lives under /api/staff/promo-codes:
  //   GET    /staff/promo-codes        — list (active + archived; ?active=,
  //                                      ?limit=) — Stripe-sourced (Q1=A)
  //   GET    /staff/promo-codes/:id    — retrieve single (FE detail context)
  //   POST   /staff/promo-codes        — create (PROMO-RF-F, NOT RF-E)
  //   POST   /staff/promo-codes/:id/archive — archive=Stripe active:false
  //                                      (PROMO-RF-F, NOT RF-E)
  // RBAC: staff + admin via authenticateJWT + authorizeRoles("staff","admin").
  // Unified error shape (ADR-029 D3): { success:false, error, reason } where
  // reason ∈ code_not_found|inactive|restriction_mismatch|vendor_unavailable
  // |invalid_request|internal_error. vendor_unavailable = HTTP 503, retryable.
  // ADR-029 D2 posture (do NOT re-open): customer discount math stays on
  // Stripe-hosted Checkout; this surface is staff management metadata ONLY —
  // no in-app customer discount calculation, no booking-time live lookup.
  STAFF_PROMO: {
    LIST: "/staff/promo-codes",
  },
};

// Error Messages
const ERROR_MESSAGES = {
  NETWORK_ERROR:
    "Network error. Please check your internet connection and try again.",
  SERVER_ERROR: "Server error. Please try again later.",
  UNAUTHORIZED:
    "You are not authorized to perform this action. Please log in again.",
  FORBIDDEN: "You do not have permission to access this resource.",
  NOT_FOUND: "The requested resource was not found.",
  VALIDATION_ERROR: "Please check your input and try again.",
  DEFAULT: "An unexpected error occurred. Please try again.",
};

// Export all configurations
export {
  API_CONFIG,
  AUTH_CONFIG,
  FEATURES,
  SERVICES,
  ENDPOINTS,
  ERROR_MESSAGES,
};

// Default export for convenience
export default {
  API: API_CONFIG,
  AUTH: AUTH_CONFIG,
  FEATURES,
  SERVICES,
  ENDPOINTS,
  ERRORS: ERROR_MESSAGES,
};
